// CopyRef — a reference (DEAL-…, RCPT-…, a chain hash) with the one click that
// copies it, saying "Copied" for a second.
//
// Item 70 / §5.14 / §6 "Reference": the Statement had this; Money movements
// and the Trades log printed the reference as plain text. One piece, so every
// list copies the same way and says the same word.
//
// THE RULE IT ENFORCES (§6 "Reference"): a reference is never a typed box —
// it is minted by the books, shown read-only in the success line and
// COPYABLE in lists. Muted mono, no colour of its own: it is the least
// important thing on the row until the operator needs it, and then it is one
// tap away. The confirmation is the word "Copied", not a colour change.
//
// Usage:
//   <CopyRef value={row.reference} />
//   <CopyRef value={deal.deal_id} className="text-[11px]" />

import { useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';

const SHOW_MS = 1000;

export function CopyRef({ value, label, className = '', tone = 'muted' }: {
  value: string;
  /** What the button shows; the full value is what it copies and what the
   *  tooltip carries. Defaults to the value itself. */
  label?: string;
  className?: string;
  /** 'neg' draws the reference in the negative colour, normal AND hover — an address-poisoning look-alike (kit's Address).
   *  A colour class passed in `className` cannot do it: both it and the muted one set `color`, and the stylesheet's order,
   *  not the class list's, decides which wins (Astra, 21 Oct). */
  tone?: 'muted' | 'neg';
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const copy = () => {
    const done = () => {
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), SHOW_MS);
    };
    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(value).then(done, () => { /* refused — nothing to say */ });
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      title={`${value}\nClick to copy`}
      aria-label={`Copy ${value}`}
      className={`tap-44 group inline-flex items-center gap-1 max-w-full text-meta font-mono ${tone === 'neg' ? 'text-neg hover:text-neg' : 'text-tg-hint hover:text-tg-text'} ${className}`}
    >
      <span className="truncate min-w-0">{label ?? value}</span>
      {copied
        ? <span className="inline-flex items-center gap-0.5 shrink-0 font-sans text-pos"><Check size={11} />Copied</span>
        : <Copy size={11} className="shrink-0 opacity-0 group-hover:opacity-100" />}
    </button>
  );
}
